/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useState, useContext, useEffect, useRef, useCallback, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { extend } from '@thd-nucleus/data-sources';
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Typography
} from '@one-thd/sui-atomic-components';
import { ConfirmedFilled } from '@one-thd/sui-icons';
import { SmartFilterWrapper } from '@thd-olt-component-react/smart-filter';
import {
  BROWSE_ALL_REFINEMENT,
  PLP_SPEED_DIMENSION_LABEL
} from '../../utils/constants';
import {
  isPlpDimensionLabel,
  getChipText
} from '../../utils/fulfillment-helpers';
import {
  getAppliedRefinements,
  getRefinementsByDimension,
  getSortNameValue,
  hasFitCompatibility
} from '../../product-results-helpers';
import { StandardDimension } from '../Dimensions/StandardDimension';
import { LinkDimension } from '../Dimensions/LinkDimension';
import { RatingRefinement } from '../Refinements/RatingRefinement';
import { VisualDimension } from '../Dimensions/VisualDimension';
import { AppliedRefinement } from '../Refinements/AppliedRefinement';
import { NumericRangeDimension } from '../Dimensions/NumericRangeDimension';
import { FulfillmentDimension } from '../Dimensions/FulfillmentDimension';
import OnDisplayContext from '../OnDisplayContext';
import { ColorSwatchDimension } from '../Dimensions/ColorSwatchDimension';
import { ResultsSortBy } from '../ResultsSortBy';
import { ProductRefinementCount } from '../ProductRefinementCount';
import { Spinner } from '../Spinner';
import { useSearchModelData } from '../../hooks/useSearchModelData';

const AccordionHeaderComponent = ({ label, hasActiveFilters, subheader }) => {
  const isSmartFilterSpinner = Boolean(label.toUpperCase() === 'SMART FILTERS');
  return (
    <AccordionHeader
      aria-controls={`accordion-${label}-content`}
      id={`accordion-${label}-header`}
      data-testid={`accordion-${label}-header`}
    >
      <div className="sui-flex sui-items-center sui-block">
        {
          isSmartFilterSpinner && (
            <div className="sui-flex sui-items-center sui-justify-center sui-mr-1 sui-w-7 sui-h-7">
              <Spinner />
            </div>
          )
        }

        <Typography component="div" variant="body-lg" weight="bold" height="tight">
          {/* H2 element required for SEO significance. Reach out to SEO team if wanting to change */}
          <h2 className="sui-inline-block" data-group={label}>{label}</h2>
          {isSmartFilterSpinner
            && (
              <div className="sui-inline-block sui-ml-1 sui-align-top">
                <Typography color="medium" variant="body-xs">Beta</Typography>
              </div>
            )}
          {hasActiveFilters(label) && (
            <span className="sui-ml-2 sui-absolute sui-mt-2px">
              <ConfirmedFilled size="small" />
            </span>
          )}
          { label === PLP_SPEED_DIMENSION_LABEL && (
            <div className="sui-mt-1">
              <Typography variant="body-base" color="subtle">
                {subheader}
              </Typography>
            </div>
          )}
        </Typography>
      </div>
    </AccordionHeader>
  );
};

AccordionHeaderComponent.propTypes = {
  label: PropTypes.string.isRequired,
  hasActiveFilters: PropTypes.func,
  subheader: PropTypes.string
};

AccordionHeaderComponent.defaultProps = {
  hasActiveFilters: () => false,
  subheader: ''
};

export const DrawerAndAppliedRefinements = ({
  appliedDimensions,
  breadCrumbs,
  clearAll,
  enableMultiStore,
  isOpen,
  metadata,
  multiStoreSelection,
  normalizedDimensions,
  onDimensionsChange,
  onDisplayData,
  onMultiStoreSelectionChange,
  onRefinementChange,
  onRefinementSelected,
  onSortByChange,
  toggleDrawer,
  productCount,
  smartFilterProperties,
  isPlpSpeedEnabled
}) => {
  const { onDisplayClick } = useContext(OnDisplayContext);

  const [fulfillmentSubheader, setFulfillmentSubheader] = useState(BROWSE_ALL_REFINEMENT.label);

  const { stores } = metadata || {};
  const storeName = stores?.storeName;

  const { data } = useSearchModelData();
  const searchReportData = data?.searchModel?.searchReport || {};
  const { keyword = '', sortBy = '', sortOrder = '' } = searchReportData;

  const desktopSort = useRef(getSortNameValue({
    key: (sortBy && sortBy.toLowerCase()) || '',
    order: (sortOrder && sortOrder.toLowerCase()) || '',
    keyword
  }).value);

  const handleSortOrderChange = (sortorder) => {
    desktopSort.current = `${sortorder.output.sortby}:${sortorder.output.sortorder}`;
  };

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('sortorder.value', handleSortOrderChange);
  }, [desktopSort]);

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const headerTitle = isMobile ? 'Sort & Filter' : 'Filters';

  const filteredAppliedDimensions = useMemo(() => appliedDimensions?.filter(
    (dimension) => dimension?.label !== BROWSE_ALL_REFINEMENT.label
  ), [appliedDimensions]);
  const appliedRefinements = useMemo(
    () => getAppliedRefinements(filteredAppliedDimensions),
    [filteredAppliedDimensions]
  );

  const onClearAllClick = (event) => {
    event.preventDefault();
    clearAll();
  };

  const closeDrawer = () => {
    toggleDrawer(false);
  };

  const hasActiveFilters = (label) => {
    if (appliedDimensions?.length < 0) {
      return false;
    }
    if (onDisplayClick && label === 'Get It Fast') {
      return true;
    }
    return appliedDimensions.find((dim) => dim.label.toUpperCase() === label.toUpperCase()
      && dim.refinements.length > 0) !== undefined;
  };

  const handleFulfillmentChange = useCallback(({
    refinement,
    dimension,
    subHeaderLabel,
    removeRefinementChip = false
  }) => {
    setFulfillmentSubheader(subHeaderLabel);
    if (typeof onRefinementSelected === 'function') {
      onRefinementSelected({ refinement, dimension, removeRefinementChip });
    }
  }, [onRefinementSelected]);

  const handleFulfillmentChipRemoval = useCallback(({ refinement, dimension, subHeaderLabel }) => {
    // Pending: If a chip is removed it does not reappear when reopening the drawer, even if the change is not submitted
    onRefinementSelected({ refinement, dimension, subHeaderLabel, removeRefinementChip: true });
  }, [onRefinementSelected]);

  const { categoryId,
    handleSmartFilterSelectedRefinements,
    selectedDimensions,
    onSmartFilterResponse } = smartFilterProperties || {};

  const generateDrawerDimensions = () => {
    return (
      <>
        {normalizedDimensions.map((dimension) => {
          const {
            label, dimensionId, isVisualDimension, isNumericFilter, isColorSwatch
          } = dimension;

          const hasSearchBar = ['BRAND', 'COLOR FAMILY', 'PATTERN TYPE', 'THEME', 'FEATURED KEYWORDS',
            'CUSTOMER SEARCHED KEYWORDS', 'POPULAR KEYWORDS'].indexOf(label.toUpperCase()) > -1;
          const hasFitCompatibilityEnabled = hasFitCompatibility(label);

          if (isVisualDimension) {
            return (
              <React.Fragment key={dimensionId}>
                <Accordion noBorders>
                  <AccordionHeaderComponent label={dimension.label} hasActiveFilters={hasActiveFilters} />
                  <AccordionBody id={`accordion-${label}-body`}>
                    <VisualDimension
                      key={dimensionId}
                      onChange={onRefinementSelected}
                      dimension={dimension}
                      selectedRefinements={getRefinementsByDimension(appliedDimensions, dimension)}
                    />
                  </AccordionBody>
                </Accordion>
                <div className="sui-w-full sui-border-primary sui-border-b-1 sui-border-solid sui-mb-2" />
              </React.Fragment>
            );
          }

          if (isNumericFilter) {
            return (
              <React.Fragment key={dimensionId}>
                <Accordion noBorders>
                  <AccordionHeaderComponent label={dimension.label} hasActiveFilters={hasActiveFilters} />
                  <AccordionBody id={`accordion-${label}-body`}>
                    <NumericRangeDimension
                      dimension={dimension}
                      fitCompatibility={hasFitCompatibility}
                      enableMultiStore={enableMultiStore}
                      key={dimensionId}
                      metadata={metadata}
                      multiStoreSelection={multiStoreSelection}
                      onChange={onRefinementSelected}
                      onDisplayData={onDisplayData}
                      onMultiStoreSelectionChange={onMultiStoreSelectionChange}
                      selectedRefinements={getRefinementsByDimension(appliedDimensions, dimension)}
                    />
                  </AccordionBody>
                </Accordion>
                <div className="sui-w-full sui-border-primary sui-border-b-1 sui-border-solid sui-mb-2" />
              </React.Fragment>
            );
          }

          if (isColorSwatch) {
            return (
              <React.Fragment key={dimensionId}>
                <Accordion noBorders>
                  <AccordionHeaderComponent label={dimension.label} hasActiveFilters={hasActiveFilters} />
                  <AccordionBody id={`accordion-${label}-body`}>
                    <ColorSwatchDimension
                      collapsed
                      dimension={dimension}
                      drawer
                      isActive={hasActiveFilters(label)}
                      key={dimensionId}
                      multiFacet
                      onChange={onRefinementSelected}
                      selectedRefinements={getRefinementsByDimension(appliedDimensions, dimension)}
                    />
                  </AccordionBody>
                </Accordion>
                <div className="sui-w-full sui-border-primary sui-border-b-1 sui-border-solid sui-mb-2" />
              </React.Fragment>
            );
          }

          switch (label) {
          case 'Category':
            return (
              <React.Fragment key={dimensionId}>
                <Accordion noBorders>
                  <AccordionHeaderComponent label={dimension.label} />
                  <AccordionBody className="!sui-p-0" id={`accordion-${label}-body`}>
                    <LinkDimension
                      breadCrumbs={breadCrumbs}
                      drawer
                      collapsed
                      dimension={dimension}
                      key={dimensionId}
                      onChange={onRefinementChange}
                      multiFacet
                    />
                  </AccordionBody>
                </Accordion>
                <div className="sui-w-full sui-border-primary sui-border-b-1 sui-border-solid sui-mb-2" />
              </React.Fragment>
            );
          case 'Review Rating':
            return (
              <React.Fragment key={dimensionId}>
                <Accordion noBorders>
                  <AccordionHeaderComponent label={dimension.label} hasActiveFilters={hasActiveFilters} />
                  <AccordionBody id={`accordion-${label}-body`}>
                    <RatingRefinement
                      key={`sec-filters-refinement-${dimension.label}`}
                      dimension={dimension}
                      onItemSelection={onRefinementSelected}
                      selectedRefinements={appliedDimensions}
                      secondaryDrawer
                    />
                  </AccordionBody>
                </Accordion>
                <div className="sui-w-full sui-border-primary sui-border-b-1 sui-border-solid sui-mb-2" />
              </React.Fragment>
            );
          case 'Smart Filters':
            return (
              <React.Fragment key={dimensionId}>
                <Accordion defaultExpanded noBorders>
                  <AccordionHeaderComponent label={dimension.label} hasActiveFilters={hasActiveFilters} />
                  <AccordionBody id={`accordion-${label}-body`}>
                    <SmartFilterWrapper
                      breadcrumbs={breadCrumbs}
                      categoryId={categoryId}
                      keyword={keyword || ""}
                      categoryName=""
                      onSmartFilterResponse={onSmartFilterResponse}
                      selectedDimensions={selectedDimensions}
                      onUpdateRefinement={handleSmartFilterSelectedRefinements}
                    />
                  </AccordionBody>
                </Accordion>
                <div className="sui-w-full sui-border-primary sui-border-b-1 sui-border-solid sui-mb-2" />
              </React.Fragment>
            );
          case PLP_SPEED_DIMENSION_LABEL:
            return (
              <React.Fragment key={dimensionId}>
                <Accordion noBorders defaultExpanded>
                  <AccordionHeaderComponent
                    label={dimension.label}
                    subheader={dimension.label === PLP_SPEED_DIMENSION_LABEL ? fulfillmentSubheader : undefined}
                  />
                  <AccordionBody id={`accordion-${label}-body`}>
                    <FulfillmentDimension
                      key={dimensionId}
                      dimension={dimension}
                      isOpen={isOpen}
                      onChange={handleFulfillmentChange}
                      storeName={storeName}
                      appliedDimensions={appliedDimensions}
                    />
                  </AccordionBody>
                </Accordion>
                <div className="sui-w-full sui-border-primary sui-border-b-1 sui-border-solid sui-mb-2" />
              </React.Fragment>
            );
          default:
            return (
              <React.Fragment key={dimensionId}>
                <Accordion noBorders>
                  <AccordionHeaderComponent
                    label={dimension.label}
                    hasActiveFilters={hasActiveFilters}
                  />
                  <AccordionBody id={`accordion-${label}-body`}>
                    <StandardDimension
                      hasSearchBar={hasSearchBar}
                      fitCompatibility={hasFitCompatibilityEnabled}
                      priceRange={label.toUpperCase() === 'PRICE'}
                      toggleSwitch={label.toUpperCase() === 'AVAILABILITY'}
                      drawer
                      dimension={dimension}
                      key={dimensionId}
                      onChange={onRefinementSelected}
                      onDisplayData={onDisplayData}
                      enableMultiStore={enableMultiStore}
                      onMultiStoreSelectionChange={onMultiStoreSelectionChange}
                      multiStoreSelection={multiStoreSelection}
                      multiFacet
                      isMobile={isMobile}
                      selectedRefinements={getRefinementsByDimension(appliedDimensions, dimension)}
                      parentID="secondaryDrawer"
                    />
                  </AccordionBody>
                </Accordion>
                <div className="sui-w-full sui-border-primary sui-border-b-1 sui-border-solid sui-mb-2" />
              </React.Fragment>
            );
          }
        })}
      </>
    );
  };

  const generateClearButton = () => {
    // Count total refinements across all dimensions
    const totalRefinements = appliedDimensions?.reduce((total, dimension) => {
      return total + (dimension.refinements?.length || 0);
    }, 0);

    if (totalRefinements > 1) {
      return (
        <button
          className="sui-underline sui-whitespace-nowrap sui-mr-2"
          data-testid="clear-all-sec-drawer"
          type="button"
          onClick={onClearAllClick}
          aria-label="Clear all filter selections"
        >
          Clear All
        </button>
      );
    }
    return null;
  };

  return (
    <Drawer
      open={isOpen}
      onClose={closeDrawer}
      position="left"
      data-testid="secondary-filter-drawer"
      keepMounted
      disablePortal
    >
      <DrawerHeader data-testid="drawer-header-Filters" title={headerTitle} onClose={closeDrawer} />
      <DrawerBody data-testid="drawer-body-sec">

        <section>
          {isMobile && (
            <ResultsSortBy
              drawer
              onSortChange={onSortByChange}
              isMobile
              searchModelData={data}
            />
          )}
          {generateDrawerDimensions()}
        </section>
      </DrawerBody>
      <DrawerFooter>
        {!appliedRefinements?.some((refinement) => refinement.dimensionName === 'Category') && (
          <footer className="sui-flex sui-flex-col">
            <div className="sui-flex sui-overflow-x-auto sui-gap-2 sui-pb-4">
              {generateClearButton()}
              {appliedRefinements?.map((refinement, index) => {
                const {
                  dimensionName,
                  refinementLabel,
                  refinementKey,
                  url
                } = refinement;

                const [displayedDimension, displayedRefinement] = getChipText(
                  dimensionName, refinementLabel, isPlpSpeedEnabled
                );

                return (
                  <AppliedRefinement
                    key={`drawer-${index}`}
                    index={index}
                    deselectUrl={url}
                    dimensionName={displayedDimension}
                    label={displayedRefinement}
                    refinementKey={refinementKey}
                    onChange={
                      isPlpSpeedEnabled && isPlpDimensionLabel(dimensionName, true)
                        ? handleFulfillmentChipRemoval : onRefinementSelected
                    }
                    desktopSort={desktopSort.current}
                  />
                );
              })}
            </div>
            {appliedRefinements.length > 0 && productCount === 0 && (
              <div className="sui-gap-2 sui-pb-4">
                <Typography color="warning" weight="bold">
                  Remove some filters to see more products.
                </Typography>
              </div>
            )}
          </footer>
        )}
        <footer>
          <ProductRefinementCount
            onDimensionsChange={onDimensionsChange}
            totalProductsCount={productCount}
          />
        </footer>
      </DrawerFooter>
    </Drawer>
  );
};

DrawerAndAppliedRefinements.propTypes = {
  appliedDimensions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      refinements: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          dimensionId: PropTypes.string,
          isVisualDimension: PropTypes.bool,
          isNumericFilter: PropTypes.bool,
          isColorSwatch: PropTypes.bool
        })
      )
    })
  ).isRequired,
  breadCrumbs: PropTypes.arrayOf(
    PropTypes.shape({})
  ),
  isOpen: PropTypes.bool,
  metadata: PropTypes.shape({}),
  normalizedDimensions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  onRefinementChange: PropTypes.func.isRequired,
  onRefinementSelected: PropTypes.func.isRequired,
  onSortByChange: PropTypes.func.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
  onDisplayData: PropTypes.shape({
    onDisplay: PropTypes.string,
    onDisplayChange: PropTypes.func,
    isStoreDisplay: PropTypes.string,
    itemCount: PropTypes.string
  }),
  enableMultiStore: PropTypes.bool,
  multiStoreSelection: PropTypes.string,
  onMultiStoreSelectionChange: PropTypes.func,
  onDimensionsChange: PropTypes.func.isRequired,
  clearAll: PropTypes.func.isRequired,
  productCount: PropTypes.number,
  smartFilterProperties: PropTypes.shape({
    categoryId: PropTypes.string
  }),
  isPlpSpeedEnabled: PropTypes.bool
};

DrawerAndAppliedRefinements.defaultProps = {
  breadCrumbs: [],
  isOpen: false,
  metadata: null,
  onDisplayData: {
    onDisplay: 'false',
    onDisplayChange: null,
    itemCount: null,
    isStoreDisplay: 'false'
  },
  enableMultiStore: false,
  multiStoreSelection: null,
  onMultiStoreSelectionChange: null,
  productCount: null,
  smartFilterProperties: {
    categoryId: ''
  },
  isPlpSpeedEnabled: false
};

DrawerAndAppliedRefinements.displayName = 'DrawerAndAppliedRefinements';

DrawerAndAppliedRefinements.dataModel = extend(StandardDimension);
